"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useAuth } from "@/lib/auth";
import { useMyApplications } from "@/lib/myApplications";
import { fetchUsers } from "@/lib/adminApi";
import { FlightStrip } from "@/components/FlightStrip";
import { TopBar } from "@/components/TopBar";
import { StatCard } from "@/components/StatCard";
import { SignInCard } from "@/components/SignInCard";
import { APPLICATION_TYPES, STATUS_STEPS } from "@/lib/rules";
import { ArrowUpRight, FilePlus2, Gavel, Loader2, ShieldCheck, Users } from "lucide-react";

function ApplicantDashboard() {
  const { applications, loading, refresh } = useMyApplications();

  const byStatus = STATUS_STEPS.map((s) => ({
    status: s,
    count: applications.filter((a) => a.status === s).length,
  }));
  const blocked = applications.filter((a) => a.flags.some((f) => f.level === "block")).length;

  return (
    <>
      <TopBar title="My Dashboard" subtitle="Your registrations, certificates and approvals" />
      <div className="px-6 py-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {byStatus.map(({ status, count }) => (
            <div key={status} className="rounded-sm border border-[var(--line)] bg-[var(--surface)] px-4 py-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">{status}</div>
              <div className="font-display text-[26px] font-semibold">{count}</div>
            </div>
          ))}
        </div>

        {blocked > 0 && (
          <div className="mt-3 rounded-sm border border-[var(--alert)]/40 bg-[#e8563d14] px-4 py-2.5 text-[12.5px] text-[var(--alert)]">
            {blocked} application{blocked > 1 ? "s" : ""} need attention before they can proceed.
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <h2 className="font-display text-[15px] font-medium">Your applications</h2>
          <div className="flex items-center gap-3">
            <Link href="/apply" className="flex items-center gap-1.5 text-[12.5px] text-[var(--blue)] hover:underline">
              <FilePlus2 size={13} /> New application
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16 text-[var(--muted-2)]">
            <Loader2 size={18} className="animate-spin" />
          </div>
        ) : applications.length === 0 ? (
          <div className="mt-3 rounded-sm border border-dashed border-[var(--line)] px-4 py-10 text-center text-[13px] text-[var(--muted-2)]">
            No applications yet.{" "}
            <Link href="/apply" className="text-[var(--blue)] hover:underline">
              Start one
            </Link>
            .
          </div>
        ) : (
          <div className="mt-3 flex flex-col gap-2.5">
            {applications.slice(0, 8).map((app) => (
              <FlightStrip key={app.id} app={app} onRefreshStatus={() => refresh()} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function ReviewerDashboard() {
  const { applications, loading } = useMyApplications();
  const pendingByType = APPLICATION_TYPES.map((t) => ({
    ...t,
    count: applications.filter((a) => a.type === t.key && a.status !== "Decided").length,
  }));
  const totalPending = pendingByType.reduce((sum, t) => sum + t.count, 0);

  return (
    <>
      <TopBar title="Review Dashboard" subtitle="Applications awaiting a committee decision" />
      <div className="px-6 py-6">
        {loading ? (
          <div className="flex justify-center py-16 text-[var(--muted-2)]">
            <Loader2 size={18} className="animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <StatCard icon={Gavel} label="Total pending" value={totalPending} color="amber" />
              {pendingByType.map((t) => (
                <StatCard key={t.key} icon={ShieldCheck} label={t.label} value={t.count} />
              ))}
            </div>
            <Link
              href="/committee"
              className="mt-6 flex w-fit items-center gap-1.5 rounded-sm border border-[var(--line)] px-4 py-2 text-[12.5px] text-[var(--ink)] transition-colors hover:border-[var(--blue)]"
            >
              Go to Committee Review <ArrowUpRight size={14} />
            </Link>
          </>
        )}
      </div>
    </>
  );
}

function AdminDashboard() {
  const { applications, loading: appsLoading } = useMyApplications();
  const [userCount, setUserCount] = useState<number | null>(null);

  useEffect(() => {
    fetchUsers().then((users) => setUserCount(users.length));
  }, []);

  const totalPending = applications.filter((a) => a.status !== "Decided").length;

  return (
    <>
      <TopBar title="Admin Overview" subtitle="Accounts, committee membership and system activity" />
      <div className="px-6 py-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatCard icon={Users} label="Registered users" value={userCount ?? "…"} color="blue" />
          <StatCard icon={Gavel} label="Pending applications" value={appsLoading ? "…" : totalPending} color="amber" />
          <Link
            href="/admin/users"
            className="group flex flex-col justify-between gap-3 rounded-sm border border-[var(--line)] bg-white p-4 transition-colors hover:border-[var(--blue)]/60"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-[#0b5fa512] text-[var(--blue)]">
              <Users size={16} />
            </div>
            <div>
              <div className="font-display text-[14px] font-medium">User Management</div>
              <div className="mt-0.5 text-[11.5px] text-[var(--muted-2)]">Create accounts, assign committee roles</div>
            </div>
            <ArrowUpRight size={15} className="self-end text-[var(--muted-2)] transition-colors group-hover:text-[var(--blue)]" />
          </Link>
        </div>
        <Link
          href="/committee"
          className="mt-6 flex w-fit items-center gap-1.5 rounded-sm border border-[var(--line)] px-4 py-2 text-[12.5px] text-[var(--ink)] transition-colors hover:border-[var(--blue)]"
        >
          Go to Committee Review <ArrowUpRight size={14} />
        </Link>
      </div>
    </>
  );
}

export default function Dashboard() {
  const { loading, loggedIn, me, backendConnected } = useAuth();

  if (backendConnected && loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center text-[var(--muted-2)]">
        <Loader2 size={18} className="animate-spin" />
      </div>
    );
  }

  if (backendConnected && !loggedIn) {
    return <SignInCard />;
  }

  if (me?.is_admin) return <AdminDashboard />;
  if (me?.is_committee || me?.is_staff) return <ReviewerDashboard />;
  return <ApplicantDashboard />;
}
